import type { Currency } from '@freelancer/datastore/collections';
import type { PictureImage } from '@freelancer/ui/picture';
import type { DepositMethodApi } from 'api-typings/payments/payments';

export enum PaymentsErrorType {
  ERROR = 'error', // Payments errors
  ERROR_AUTH = 'error_auth', // Auth issues
  ERROR_INIT = 'error_init', // Error on page load
  ERROR_UNKNOWN = 'error_unknown', // Unhandled exceptions
  ERROR_DATASTORE = 'error_datastore', // Datastore errors
  ERROR_POPUP = 'error_popup', // Popup error
  ERROR_UNDEFINED_DATA = 'error_undefined_data', // Undefined data error
}

export enum PaymentsEventType {
  INITIALIZED = 'initialized', // site is ready to go
  CLOSE_OVERLAY = 'close_overlay', // event to close an overlay
}

export enum PaymentsResultStatus {
  SUCCESS = 'success',
  PENDING = 'pending', // Pending Payments
  INTERRUPTED = 'interrupted', // Payments action cancelled
  REDIRECT = 'redirect', // From modal - redirecting to Payments page
}

export interface PaymentsProcessError {
  errorType:
    | PaymentsErrorType.ERROR
    | PaymentsErrorType.ERROR_AUTH
    | PaymentsErrorType.ERROR_INIT
    | PaymentsErrorType.ERROR_UNKNOWN
    | PaymentsErrorType.ERROR_POPUP;
  cta?: string;
  ctaLink?: string;
  // TODO: T267853 - errorCode & errorMessage should be related, probs one is enough
  errorCode?: string;
  errorMessage?: string;
  eventId?: string;
  paymentMethod?: string; // For displaying a better error msg.
}

export interface PaymentsDatastoreError {
  errorType: PaymentsErrorType.ERROR_DATASTORE;
  errorCode: string;
  retry(): void;
}

export type PaymentsError = PaymentsProcessError | PaymentsDatastoreError;

// These boys are used for internal event communication
// where we don't care about message, just about the type of event
export interface PaymentsEvent {
  eventType: PaymentsEventType;
}

// Use this type after user attempted a Payments and it didn't return an error
export interface PaymentsResult {
  paymentsStatus: PaymentsResultStatus;
  cta?: string;
  ctaLink?: string;
  paymentsMessage?: string;
  approvalRequested?: boolean;
  method?: DepositMethodApi;
  is3ds?: boolean;
}

export enum OverlayTypes {
  BACKEND_ERROR,
  CC_3DS,
  CC_CAPTCHA,
  CONFIRMATION_WIRE,
  CONFIRMATION_WIRE_SUCCESS,
  DEPOSIT_SUCCESS,
  EXTERNAL_POPUP,
  VERIFICATION_SUCCESS,
  PAYPAL_PENDING,
}

// This one is emitted when we want to display an overlay.
export interface PaymentsOverlay {
  brand?: PictureImage;
  ctaAction?(): void;
  onClose?(): void;
  overlayType: OverlayTypes;
  confirmationDetails?: ConfirmationDetails;
  titleText?: string;
  method?: DepositMethodApi;
  cartId?: number;
}

export interface ConfirmationDetails {
  invoiceId: number;
  userId: number;
  amount: number;
  currency: Currency;
  cartId?: number;
  bankInfo: string;
}

// messages to be transmitted over PaymentsOverlay subject pipe
export enum PaymentsOverlayAction {
  CLOSE,
}

export type PaymentsMessage =
  | PaymentsOverlay
  | PaymentsResult
  | PaymentsEvent
  | PaymentsError;

export function isPaymentsEvent(
  paymentsResult: PaymentsMessage,
): paymentsResult is PaymentsEvent {
  return (paymentsResult as PaymentsEvent).eventType !== undefined;
}

export function isPaymentsResult(
  paymentsResult: PaymentsMessage,
): paymentsResult is PaymentsResult {
  return (paymentsResult as PaymentsResult).paymentsStatus !== undefined;
}

export function isPaymentsOverlay(
  paymentsResult: PaymentsMessage,
): paymentsResult is PaymentsOverlay {
  return (paymentsResult as PaymentsOverlay).overlayType !== undefined;
}

export function isPaymentsError(
  paymentsResult: PaymentsMessage,
): paymentsResult is PaymentsError {
  return (paymentsResult as PaymentsError).errorType !== undefined;
}
